import React, { FC, MutableRefObject, ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CMECapabilities } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { CMEResponse } from '../../../NonReact/Types/Types'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { ANY_NUMBER_OF_TELSYS_AVAILABLE, CapabilityBitFlags } from '../Constants'
import {
    CapabilitiesFieldNames,
    cxaBroadworksFieldName,
    cxaTeamsFieldName,
    cxaWebexFieldName,
    cxiBroadworksFieldName,
    cxiTeamsFieldName,
    cxiWebexFieldName,
    cxsBroadworksFieldName,
    cxsWebexFieldName,
    cxsTeamsFieldName,
    cxrBroadworksFieldName,
    cxrWebexFieldName,
    cxrTeamsFieldName,
    aiAssistFieldName,
    cxtBroadworksFieldName,
    cxtWebexFieldName,
    cxtTeamsFieldName,
    enterpriseFieldName,
    legacyFieldName,
    legacyTelephonyFieldName,
    maxTelsysCountFieldName,
    showUiVersionToggleFieldName
} from '../FieldName'
import { CapabilitiesEnabled } from '../Types'
import {
    CapabilityPlatform,
    CapabilityProduct,
    DisabledCapabilities,
    isAiAssistAvailable,
    isCapabilityAvailable,
    isCxtQualified,
    useDisabledCapabilities
} from './CapabilityAvailability'
import { TelSysTypes } from '../../TelSysConfiguration/Constants/Constants'
import { isCxiTelSysType } from '../../Common/Utils/CxiEntitlementRules'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import FormRow from '../../Common/Components/FormRow'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'

type Props = {
    cmeID?: string
    isViewOnly?: boolean
    isAdmin: boolean
    parentCME: Nullable<CMEResponse>
}

/**
 * One checkbox on the "Which Telephony Server types are available?" matrix.
 */
type CapabilityCell = {
    fieldName: string
    capability: keyof CapabilitiesEnabled
    onChange?: (value: boolean) => void
}

/**
 * One TelSys type row on the capability matrix. Products with no cell are never available for the row.
 */
type CapabilityRow = {
    key: string
    label: ReactNode
    platform?: CapabilityPlatform
    telSysType?: TelSysTypes
    cells: Partial<Record<CapabilityProduct, CapabilityCell>>
}

const CAPABILITY_PRODUCTS: readonly CapabilityProduct[] = [
    CapabilityProduct.CXA,
    CapabilityProduct.CXI,
    CapabilityProduct.CXS,
    CapabilityProduct.CXR,
    CapabilityProduct.CXT
]

/** The products CX Transcribe can depend on. */
type TranscribeQualifier = CapabilityProduct.CXS | CapabilityProduct.CXR

const TRANSCRIBE_QUALIFIERS: readonly TranscribeQualifier[] = [CapabilityProduct.CXS, CapabilityProduct.CXR]

const TRANSCRIBE_PLATFORMS: readonly CapabilityPlatform[] = [
    CapabilityPlatform.BROADWORKS,
    CapabilityPlatform.WEBEX,
    CapabilityPlatform.TEAMS
]

const PRODUCT_LABELS: Record<CapabilityProduct, string> = {
    [CapabilityProduct.CXA]: CMECapabilities.CxAnalytics,
    [CapabilityProduct.CXI]: CMECapabilities.CxIntegration,
    [CapabilityProduct.CXS]: CMECapabilities.CxScore,
    [CapabilityProduct.CXR]: CMECapabilities.CxRecord,
    [CapabilityProduct.CXT]: CMECapabilities.CxTranscribe
}

type ConfigControlledField = {
    fieldName: string
    platform: CapabilityPlatform
    product: CapabilityProduct
}

/**
 * Matrix fields whose availability comes from the generic config.
 */
const CONFIG_CONTROLLED_FIELDS: readonly ConfigControlledField[] = [
    { fieldName: cxaBroadworksFieldName, platform: CapabilityPlatform.BROADWORKS, product: CapabilityProduct.CXA },
    { fieldName: cxiBroadworksFieldName, platform: CapabilityPlatform.BROADWORKS, product: CapabilityProduct.CXI },
    { fieldName: cxsBroadworksFieldName, platform: CapabilityPlatform.BROADWORKS, product: CapabilityProduct.CXS },
    { fieldName: cxrBroadworksFieldName, platform: CapabilityPlatform.BROADWORKS, product: CapabilityProduct.CXR },
    { fieldName: cxtBroadworksFieldName, platform: CapabilityPlatform.BROADWORKS, product: CapabilityProduct.CXT },
    { fieldName: cxaWebexFieldName, platform: CapabilityPlatform.WEBEX, product: CapabilityProduct.CXA },
    { fieldName: cxiWebexFieldName, platform: CapabilityPlatform.WEBEX, product: CapabilityProduct.CXI },
    { fieldName: cxsWebexFieldName, platform: CapabilityPlatform.WEBEX, product: CapabilityProduct.CXS },
    { fieldName: cxrWebexFieldName, platform: CapabilityPlatform.WEBEX, product: CapabilityProduct.CXR },
    { fieldName: cxtWebexFieldName, platform: CapabilityPlatform.WEBEX, product: CapabilityProduct.CXT },
    { fieldName: cxaTeamsFieldName, platform: CapabilityPlatform.TEAMS, product: CapabilityProduct.CXA },
    { fieldName: cxiTeamsFieldName, platform: CapabilityPlatform.TEAMS, product: CapabilityProduct.CXI },
    { fieldName: cxsTeamsFieldName, platform: CapabilityPlatform.TEAMS, product: CapabilityProduct.CXS },
    { fieldName: cxrTeamsFieldName, platform: CapabilityPlatform.TEAMS, product: CapabilityProduct.CXR },
    { fieldName: cxtTeamsFieldName, platform: CapabilityPlatform.TEAMS, product: CapabilityProduct.CXT }
]

type NotAvailableCapabilityProps = {
    label: string
}

/**
 * Greyed-out placeholder for a product/TelSys type combination that is not available yet, so it reads
 * differently from both a ticked and an unticked checkbox.
 */
const NotAvailableCapability: FC<NotAvailableCapabilityProps> = ({
    label
}: NotAvailableCapabilityProps): ReactElement => (
    <span className='ct-not-available' role='img' aria-label={label} title={label} />
)

export const Capabilities: FC<Props> = ({ cmeID, isViewOnly, isAdmin, parentCME }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(CMECapabilities.NAME)

    const isNewCME: boolean = IsStringEmpty(cmeID)

    const { watch, setValue, trigger }: UseFormMethods = useFormContext()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [capabilitiesEnabled, setCapabilitiesEnabled]: ReactState<Nullable<CapabilitiesEnabled>> =
        useState<Nullable<CapabilitiesEnabled>>()

    const isRootCME: boolean = cmeID === DEFAULT_CME_ID

    const disabledCapabilities: Nullable<DisabledCapabilities> = useDisabledCapabilities()

    const cxaBroadworksValue: boolean = watch(cxaBroadworksFieldName, false)
    const cxaWebexValue: boolean = watch(cxaWebexFieldName, false)
    const cxaTeamsValue: boolean = watch(cxaTeamsFieldName, false)

    const legacyTelphonyValue: boolean = watch(legacyTelephonyFieldName, false)
    const legacyValue: boolean = watch(legacyFieldName, false)
    const enterpriseValue: boolean = watch(enterpriseFieldName, false)

    const legacyLicenceDisabled: boolean = useMemo(
        (): boolean => !cxaBroadworksValue && !!cxaWebexValue && !legacyTelphonyValue,
        [cxaBroadworksValue, cxaWebexValue, legacyTelphonyValue]
    )

    const licencesDisabled: boolean = useMemo(
        (): boolean => !cxaBroadworksValue && !cxaWebexValue && !legacyTelphonyValue,
        [cxaBroadworksValue, cxaWebexValue, legacyTelphonyValue]
    )

    /**
     * A new CME is validated on every requested bit, so a combination the config marks unavailable must not be
     * submitted as on. An existing CME keeps its saved bits, as the BE only rejects newly turned-on ones.
     */
    const clearUnavailableForNewCME = (): void => {
        if (!isNewCME) {
            return
        }

        CONFIG_CONTROLLED_FIELDS.forEach(({ fieldName, platform, product }: ConfigControlledField): void => {
            if (!isCapabilityAvailable(disabledCapabilities, platform, product)) {
                setValue(fieldName, false)
            }
        })
    }

    useEffect((): void => {
        if (isVarNotEmpty(parentCME?.Capabilities)) {
            const parentCapabilities: CapabilitiesEnabled = {
                cxaBroadworks: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_BROADWORKS),
                cxaWebex: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_WEBEX),
                cxaTeams: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_TEAMS),
                cxiBroadworks: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXI_BROADWORKS),
                cxiWebex: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXI_WEBEX),
                cxiTeams: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXI_TEAMS),
                cxsBroadworks: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXS_BROADWORKS),
                cxsWebex: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXS_WEBEX),
                cxsTeams: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXS_TEAMS),
                cxrBroadworks: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXR_BROADWORKS),
                cxrWebex: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXR_WEBEX),
                cxrTeams: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXR_TEAMS),
                aiAssist: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.AI_ASSIST),
                cxtBroadworks: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXT_BROADWORKS),
                cxtWebex: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXT_WEBEX),
                cxtTeams: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXT_TEAMS),
                legacyTelephony: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.LEGACY_TELEPHONY),
                legacy: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_LEGACY),
                enterprise: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.ENTERPRISE)
            }

            if (isMounted.current) {
                setCapabilitiesEnabled(parentCapabilities)

                const setCapabilityValue = (capability: string, isEnabled: boolean): void => {
                    if (!isEnabled || isNewCME) {
                        setValue(capability, isEnabled)
                    }
                }

                setTimeout((): void => {
                    setCapabilityValue(cxaBroadworksFieldName, parentCapabilities.cxaBroadworks)
                    setCapabilityValue(cxaWebexFieldName, parentCapabilities.cxaWebex)
                    setCapabilityValue(legacyTelephonyFieldName, parentCapabilities.legacyTelephony)
                    setCapabilityValue(legacyFieldName, parentCapabilities.legacy)
                    setCapabilityValue(enterpriseFieldName, parentCapabilities.enterprise)
                    setCapabilityValue(cxaTeamsFieldName, parentCapabilities.cxaTeams)
                    setCapabilityValue(cxiBroadworksFieldName, parentCapabilities.cxiBroadworks)
                    setCapabilityValue(cxiTeamsFieldName, parentCapabilities.cxiTeams)
                    setCapabilityValue(cxiWebexFieldName, parentCapabilities.cxiWebex)
                    setCapabilityValue(cxsBroadworksFieldName, parentCapabilities.cxsBroadworks)
                    setCapabilityValue(cxsWebexFieldName, parentCapabilities.cxsWebex)
                    setCapabilityValue(cxsTeamsFieldName, parentCapabilities.cxsTeams)
                    setCapabilityValue(cxrBroadworksFieldName, parentCapabilities.cxrBroadworks)
                    setCapabilityValue(cxrWebexFieldName, parentCapabilities.cxrWebex)
                    setCapabilityValue(cxrTeamsFieldName, parentCapabilities.cxrTeams)
                    setCapabilityValue(aiAssistFieldName, parentCapabilities.aiAssist)
                    setCapabilityValue(cxtBroadworksFieldName, parentCapabilities.cxtBroadworks)
                    setCapabilityValue(cxtWebexFieldName, parentCapabilities.cxtWebex)
                    setCapabilityValue(cxtTeamsFieldName, parentCapabilities.cxtTeams)
                    clearUnavailableForNewCME()
                })
            }
        } else if (isMounted.current) {
            setCapabilitiesEnabled({
                cxaBroadworks: true,
                cxaWebex: true,
                cxaTeams: true,
                cxiBroadworks: true,
                cxiWebex: true,
                cxiTeams: true,
                cxsBroadworks: true,
                cxsWebex: true,
                cxsTeams: true,
                cxrBroadworks: true,
                cxrWebex: true,
                cxrTeams: true,
                aiAssist: true,
                cxtBroadworks: true,
                cxtWebex: true,
                cxtTeams: true,
                legacyTelephony: true,
                legacy: true,
                enterprise: true
            })

            setTimeout(clearUnavailableForNewCME)
        }
    }, [parentCME, isNewCME, disabledCapabilities])

    const onChange = (): void => {
        if (watch(cxaWebexFieldName) || watch(cxaTeamsFieldName)) {
            setValue(enterpriseFieldName, true)

            if (!watch(cxaBroadworksFieldName) && !watch(legacyTelephonyFieldName)) {
                setValue(legacyFieldName, false)
            }
        }

        if (
            !watch(legacyTelephonyFieldName) &&
            !watch(cxaWebexFieldName) &&
            !watch(cxaBroadworksFieldName) &&
            !watch(cxaTeamsFieldName)
        ) {
            setValue(enterpriseFieldName, false)
            setValue(legacyFieldName, false)
        }

        setTimeout((): void => {
            trigger(legacyFieldName)
            trigger(enterpriseFieldName)
        })
    }

    const isCxiCapabilityCheckboxDisabled = (capability: keyof CapabilitiesEnabled): boolean => {
        if (isAdmin && isRootCME) {
            return false
        }

        const isCapabilityEnabled: boolean = capabilitiesEnabled?.[capability] ?? false
        return !isCapabilityEnabled
    }

    const onTelServerCapabilityChange = (): void => {
        onChange()

        setTimeout((): void => {
            trigger(legacyFieldName)
            trigger(enterpriseFieldName)
        })
    }

    // The CX Score, CX Record and CX Transcribe fields of each TelSys type, for the CX Transcribe rule.
    const transcribeFieldNames: Record<
        CapabilityPlatform,
        Record<TranscribeQualifier | CapabilityProduct.CXT, string>
    > = {
        [CapabilityPlatform.BROADWORKS]: {
            [CapabilityProduct.CXS]: cxsBroadworksFieldName,
            [CapabilityProduct.CXR]: cxrBroadworksFieldName,
            [CapabilityProduct.CXT]: cxtBroadworksFieldName
        },
        [CapabilityPlatform.WEBEX]: {
            [CapabilityProduct.CXS]: cxsWebexFieldName,
            [CapabilityProduct.CXR]: cxrWebexFieldName,
            [CapabilityProduct.CXT]: cxtWebexFieldName
        },
        [CapabilityPlatform.TEAMS]: {
            [CapabilityProduct.CXS]: cxsTeamsFieldName,
            [CapabilityProduct.CXR]: cxrTeamsFieldName,
            [CapabilityProduct.CXT]: cxtTeamsFieldName
        }
    }

    /**
     * The CX Score and CX Record products ticked for a TelSys type.
     *
     * @param platform The TelSys type.
     * @returns The ticked products.
     */
    const tickedTranscribeProducts = (platform: CapabilityPlatform): Set<CapabilityProduct> =>
        new Set(
            TRANSCRIBE_QUALIFIERS.filter(
                (product: TranscribeQualifier): boolean => !!watch(transcribeFieldNames[platform][product], false)
            )
        )

    /**
     * Whether CX Transcribe is ticked for a TelSys type.
     *
     * @param platform The TelSys type.
     * @returns True when it is ticked.
     */
    const isTranscribeTicked = (platform: CapabilityPlatform): boolean =>
        !!watch(transcribeFieldNames[platform][CapabilityProduct.CXT], false)

    /**
     * Whether a ticked CX Score / CX Record box is the last product letting CX Transcribe stay on for its TelSys
     * type, so it can't be unticked until CX Transcribe is.
     *
     * @param platform The TelSys type.
     * @param product The CX Score or CX Record product.
     * @returns True when unticking it would leave CX Transcribe with nothing it depends on.
     */
    const isLastTranscribeQualifier = (platform: CapabilityPlatform, product: TranscribeQualifier): boolean => {
        const ticked: Set<CapabilityProduct> = tickedTranscribeProducts(platform)

        if (!ticked.has(product)) return false

        ticked.delete(product)

        return !isCxtQualified(platform, ticked)
    }

    // CX Transcribe is only on alongside a product it depends on. The form can still hold it without one, when a
    // parent CME forces that product off or the CME was saved that way, so clear it there. The user didn't make the
    // change, so it isn't marked dirty, and the send mapper drops the bit whatever the form holds.
    const unqualifiedTranscribePlatforms: string = TRANSCRIBE_PLATFORMS.filter(
        (platform: CapabilityPlatform): boolean =>
            isTranscribeTicked(platform) && !isCxtQualified(platform, tickedTranscribeProducts(platform))
    ).join(',')

    useEffect((): void => {
        if (IsStringEmpty(unqualifiedTranscribePlatforms)) return

        unqualifiedTranscribePlatforms.split(',').forEach((platform: string): void => {
            setValue(transcribeFieldNames[platform as CapabilityPlatform][CapabilityProduct.CXT], false)
        })
    }, [unqualifiedTranscribePlatforms])

    const capabilityRows: CapabilityRow[] = [
        {
            key: CapabilityPlatform.BROADWORKS,
            label: language.get(CMECapabilities.Broadworks),
            platform: CapabilityPlatform.BROADWORKS,
            telSysType: TelSysTypes.BROADSOFT_BROADWORKS,
            cells: {
                [CapabilityProduct.CXA]: {
                    fieldName: cxaBroadworksFieldName,
                    capability: 'cxaBroadworks',
                    onChange: onTelServerCapabilityChange
                },
                [CapabilityProduct.CXI]: {
                    fieldName: cxiBroadworksFieldName,
                    capability: 'cxiBroadworks',
                    onChange: onTelServerCapabilityChange
                },
                [CapabilityProduct.CXS]: { fieldName: cxsBroadworksFieldName, capability: 'cxsBroadworks' },
                [CapabilityProduct.CXR]: { fieldName: cxrBroadworksFieldName, capability: 'cxrBroadworks' },
                [CapabilityProduct.CXT]: { fieldName: cxtBroadworksFieldName, capability: 'cxtBroadworks' }
            }
        },
        {
            key: CapabilityPlatform.WEBEX,
            label: language.get(CMECapabilities.Webex),
            platform: CapabilityPlatform.WEBEX,
            telSysType: TelSysTypes.WEBEX_CALLING,
            cells: {
                [CapabilityProduct.CXA]: {
                    fieldName: cxaWebexFieldName,
                    capability: 'cxaWebex',
                    onChange: onTelServerCapabilityChange
                },
                [CapabilityProduct.CXI]: {
                    fieldName: cxiWebexFieldName,
                    capability: 'cxiWebex',
                    onChange: onTelServerCapabilityChange
                },
                [CapabilityProduct.CXS]: { fieldName: cxsWebexFieldName, capability: 'cxsWebex' },
                [CapabilityProduct.CXR]: { fieldName: cxrWebexFieldName, capability: 'cxrWebex' },
                [CapabilityProduct.CXT]: { fieldName: cxtWebexFieldName, capability: 'cxtWebex' }
            }
        },
        {
            key: CapabilityPlatform.TEAMS,
            label: language.get(CMECapabilities.Teams),
            platform: CapabilityPlatform.TEAMS,
            telSysType: TelSysTypes.MS_TEAMS,
            cells: {
                [CapabilityProduct.CXA]: {
                    fieldName: cxaTeamsFieldName,
                    capability: 'cxaTeams',
                    onChange: onTelServerCapabilityChange
                },
                [CapabilityProduct.CXI]: {
                    fieldName: cxiTeamsFieldName,
                    capability: 'cxiTeams',
                    onChange: onTelServerCapabilityChange
                },
                [CapabilityProduct.CXS]: { fieldName: cxsTeamsFieldName, capability: 'cxsTeams' },
                [CapabilityProduct.CXR]: { fieldName: cxrTeamsFieldName, capability: 'cxrTeams' },
                [CapabilityProduct.CXT]: { fieldName: cxtTeamsFieldName, capability: 'cxtTeams' }
            }
        },
        {
            key: CapabilitiesFieldNames.LEGACY_TELEPHONY,
            label: LanguageUtils.formatLanguageEntry(language.get(CMECapabilities.LegacyTelephony)),
            cells: {
                [CapabilityProduct.CXA]: {
                    fieldName: legacyTelephonyFieldName,
                    capability: 'legacyTelephony',
                    onChange: (value: boolean): void => {
                        onChange()

                        if (value) {
                            setValue(legacyFieldName, true)
                        }

                        setTimeout((): void => {
                            trigger(legacyFieldName)
                            trigger(enterpriseFieldName)
                        })
                    }
                }
            }
        }
    ]

    const isCapabilityCellAvailable = (row: CapabilityRow, product: CapabilityProduct): boolean => {
        if (isVarNotEmpty(row.platform) && !isCapabilityAvailable(disabledCapabilities, row.platform, product)) {
            return false
        }

        return product !== CapabilityProduct.CXI || isCxiTelSysType(row.telSysType)
    }

    const renderCapabilityCell = (row: CapabilityRow, product: CapabilityProduct): ReactElement => {
        const cell: Definable<CapabilityCell> = row.cells[product]

        if (IsVarEmpty(cell) || !isCapabilityCellAvailable(row, product)) {
            return <NotAvailableCapability label={language.get(CMECapabilities.CapabilityNotAvailable)} />
        }

        const isParentBlocked: boolean =
            product === CapabilityProduct.CXI
                ? isCxiCapabilityCheckboxDisabled(cell.capability)
                : !capabilitiesEnabled?.[cell.capability]

        // CX Transcribe can't be ticked until a qualifying product is.
        const isTranscribeBlocked: boolean =
            product === CapabilityProduct.CXT &&
            isVarNotEmpty(row.platform) &&
            !isCxtQualified(row.platform, tickedTranscribeProducts(row.platform))

        // While CX Transcribe is ticked, the last product it depends on can't be unticked.
        const isTranscribeQualifierLocked: boolean =
            (product === CapabilityProduct.CXS || product === CapabilityProduct.CXR) &&
            isVarNotEmpty(row.platform) &&
            isTranscribeTicked(row.platform) &&
            isLastTranscribeQualifier(row.platform, product)

        let transcribeHint: Definable<string>

        if (isTranscribeBlocked) {
            transcribeHint = language.get(
                row.platform === CapabilityPlatform.BROADWORKS
                    ? CMECapabilities.CxTranscribeNeedsCxs
                    : CMECapabilities.CxTranscribeNeedsCxsOrCxr
            )
        } else if (isTranscribeQualifierLocked) {
            transcribeHint = language.get(CMECapabilities.CxTranscribeNeedsThis)
        }

        const checkboxElement: ReactElement = (
            <RouterCheckbox
                name={cell.fieldName}
                disabled={isParentBlocked || isTranscribeBlocked || isTranscribeQualifierLocked || !!isViewOnly}
                onChange={cell.onChange}
            />
        )

        // The checkbox only shows a title on its label, and matrix cells have none, so the hint goes on a wrapper.
        return isVarNotEmpty(transcribeHint) ? (
            <span className='ct-capability-hint' title={transcribeHint}>
                {checkboxElement}
            </span>
        ) : (
            checkboxElement
        )
    }

    const resetToAny =
        (isResetOnEmpty?: boolean): ((value?: string) => void) =>
        (value?: string): void => {
            if (
                isNaN(Number(value)) ||
                (isResetOnEmpty && IsStringEmpty(value)) ||
                (isVarNotEmpty(value) && !isNaN(Number(value)) && Number(value) < 0)
            ) {
                setValue(maxTelsysCountFieldName, ANY_NUMBER_OF_TELSYS_AVAILABLE)
            }
        }

    return (
        <div className='capabilities-tab'>
            <div className='ct-max-telsys-count'>
                <FormRow>
                    <OutlinedTextInput
                        inputName={maxTelsysCountFieldName}
                        labelLeft={language.get(CMECapabilities.MaxTelsysCount)}
                        onClick={({ currentTarget }: React.MouseEvent<Element, MouseEvent>): void => {
                            currentTarget?.querySelector('input')?.select()
                        }}
                        onChange={resetToAny(false)}
                        onBlur={resetToAny(true)}
                        disabled={!!isViewOnly}
                    />
                </FormRow>
            </div>
            <Heading level={HeadingLevel.TWO} className='ct-title'>
                {language.get(CMECapabilities.AvailableTelServers)}
            </Heading>
            <table className='ct-table'>
                <thead>
                    <tr>
                        <th>{language.get(CMECapabilities.Capability)}</th>
                        {CAPABILITY_PRODUCTS.map(
                            (product: CapabilityProduct): ReactElement => (
                                <th key={product} className='header-centre'>
                                    {language.get(PRODUCT_LABELS[product])}
                                </th>
                            )
                        )}
                    </tr>
                </thead>
                <tbody>
                    {capabilityRows.map(
                        (row: CapabilityRow): ReactElement => (
                            <tr key={row.key}>
                                <td>{row.label}</td>
                                {CAPABILITY_PRODUCTS.map(
                                    (product: CapabilityProduct): ReactElement => (
                                        <td key={product}>{renderCapabilityCell(row, product)}</td>
                                    )
                                )}
                            </tr>
                        )
                    )}
                </tbody>
            </table>
            <Heading level={HeadingLevel.TWO} className='ct-title'>
                {language.get(CMECapabilities.AvailableLicences)}
            </Heading>
            <table className='ct-table'>
                <thead>
                    <tr>
                        <th>{language.get(CMECapabilities.Capability)}</th>
                        <th className='header-centre'>{language.get(CMECapabilities.CapabilityEnabled)}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{LanguageUtils.formatLanguageEntry(language.get(CMECapabilities.Legacy))}</td>
                        <td>
                            <RouterCheckbox
                                name={legacyFieldName}
                                disabled={
                                    legacyLicenceDisabled ||
                                    licencesDisabled ||
                                    !capabilitiesEnabled?.legacy ||
                                    !!isViewOnly
                                }
                                onChange={() => {
                                    trigger(legacyFieldName)

                                    setTimeout((): void => {
                                        trigger(enterpriseFieldName)
                                    })
                                }}
                                rules={{
                                    validate: {
                                        isLicenceTypeRequired: (value: boolean): ValidateResult => {
                                            if (
                                                (cxaBroadworksValue ||
                                                    cxaWebexValue ||
                                                    cxaTeamsValue ||
                                                    legacyTelphonyValue) &&
                                                !value &&
                                                !enterpriseValue
                                            ) {
                                                return false
                                            }

                                            return true
                                        }
                                    }
                                }}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>{language.get(CMECapabilities.Enterprise)}</td>
                        <td>
                            <RouterCheckbox
                                name={enterpriseFieldName}
                                disabled={licencesDisabled || !capabilitiesEnabled?.enterprise || !!isViewOnly}
                                onChange={(): void => {
                                    trigger(enterpriseFieldName)

                                    setTimeout((): void => {
                                        trigger(legacyFieldName)
                                    })
                                }}
                                rules={{
                                    validate: {
                                        isLicenceTypeRequired: (value: boolean): ValidateResult => {
                                            if (
                                                (cxaBroadworksValue ||
                                                    cxaWebexValue ||
                                                    cxaTeamsValue ||
                                                    legacyTelphonyValue) &&
                                                !value &&
                                                !legacyValue
                                            ) {
                                                return false
                                            }

                                            return true
                                        }
                                    }
                                }}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
            <Heading level={HeadingLevel.TWO} className='ct-title'>
                {language.get(CMECapabilities.AvailableFeatures)}
            </Heading>
            <table className='ct-table'>
                <thead>
                    <tr>
                        <th>{language.get(CMECapabilities.Capability)}</th>
                        <th className='header-centre'>{language.get(CMECapabilities.CapabilityEnabled)}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{language.get(CMECapabilities.AiAssist)}</td>
                        <td>
                            {isAiAssistAvailable(disabledCapabilities) ? (
                                <RouterCheckbox
                                    name={aiAssistFieldName}
                                    disabled={!capabilitiesEnabled?.aiAssist || !!isViewOnly}
                                />
                            ) : (
                                <NotAvailableCapability label={language.get(CMECapabilities.CapabilityNotAvailable)} />
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
            <Heading level={HeadingLevel.TWO} className='ct-title'>
                {language.get(CMECapabilities.NewUI)}
            </Heading>
            <table className='ct-table'>
                <thead>
                    <tr>
                        <th>{language.get(CMECapabilities.Capability)}</th>
                        <th className='header-centre'>{language.get(CMECapabilities.CapabilityEnabled)}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{language.get(CMECapabilities.ClassicCxSuiteToggle)}</td>
                        <td>
                            <RouterCheckbox name={showUiVersionToggleFieldName} disabled={!!isViewOnly} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}
